import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { normalizeRef } from '@/lib/cpi';
import { getFirebaseServices } from '@/lib/firebase/client';
import { TimeFilter } from '@/types';

interface InstallEvent {
  game: string;
  cpi: number;
  eventCount: number;
  timestamp: any; // Firestore Timestamp object or string or number
  date: string;
}

interface AggregatedData {
  totalEarnings: number;
  totalInstalls: number;
  loading: boolean;
}

function getDateRangeForFilter(filter: TimeFilter): Date | null {
  const now = new Date();
  switch (filter) {
    case 'today':
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      return startOfToday;
    case '7d':
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      return sevenDaysAgo;
    case '30d':
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      return thirtyDaysAgo;
    case 'lifetime':
      return null; // No filter
  }
}

/**
 * Hook to aggregate install events from Firestore by time filter.
 * Dynamically calculates total earnings and installs without storing totals.
 * 
 * @param userId Firebase Auth UID
 * @param filter Time filter (today, 7d, 30d, lifetime)
 * @param gameFilter Optional game slug to filter by specific game
 */
export function useInstallEventsAggregation(
  userId: string | null | undefined,
  filter: TimeFilter = 'lifetime',
  gameFilter?: string,
  refFilter?: string
): AggregatedData {
  const [data, setData] = useState<AggregatedData>({
    totalEarnings: 0,
    totalInstalls: 0,
    loading: true,
  });

  useEffect(() => {
    if (!userId) {
      setData({ totalEarnings: 0, totalInstalls: 0, loading: false });
      return;
    }

    const services = getFirebaseServices();
    if (!services) {
      setData({ totalEarnings: 0, totalInstalls: 0, loading: false });
      return;
    }

    const { db } = services;
    const eventsRef = collection(db, `users/${userId}/installEvents`);

    // Build query with filters
    let constraints = [];
    
    if (gameFilter) {
      // Stored `game` values are normalized (alphanumeric lowercase). Normalize the
      // incoming gameFilter (which may be a slug like "shawarma-run") so queries match.
      const normGame = normalizeRef(gameFilter);
      constraints.push(where('game', '==', normGame));
    }

    if (refFilter) {
      // ref values in installEvents are normalized (alphanumeric lowercase). Normalize
      // the incoming refFilter so queries match the stored `ref` field.
      const normRef = normalizeRef(refFilter);
      constraints.push(where('ref', '==', normRef));
    }

    const startDate = getDateRangeForFilter(filter);
    if (startDate) {
      // Convert to Firestore Timestamp for comparison
      const timestampValue = Timestamp.fromDate(startDate);
      // Prefer querying by the event's `timestamp` field (the actual event time)
      // so "today" uses the event occurrence time rather than when it was stored.
      // If some documents don't have `timestamp`, they will be filtered client-side below.
      constraints.push(where('timestamp', '>=', timestampValue));
    }

    const q = constraints.length > 0 
      ? query(eventsRef, ...constraints)
      : query(eventsRef);

    console.log('[useInstallEventsAggregation] Query setup:', { userId, filter, gameFilter, startDate: startDate?.toISOString() });

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('[useInstallEventsAggregation] Snapshot received:', { userId, filter, gameFilter, docsCount: snapshot.docs.length });
      let totalEarnings = 0;
      let totalInstalls = 0;
      let processedCount = 0;
      let skippedCount = 0;

      snapshot.docs.forEach((doc) => {
        const event = doc.data() as InstallEvent;

        // Client-side safety: if we have a startDate, ensure the event.timestamp falls within the range.
        if (startDate) {
          let evDate: Date | null = null;
          try {
            // @ts-ignore
            if (event.timestamp && typeof event.timestamp.toDate === 'function') {
              // @ts-ignore
              evDate = event.timestamp.toDate();
            } else if (typeof event.timestamp === 'number') {
              evDate = new Date(event.timestamp);
            } else if (typeof event.date === 'string' && event.date.length === 8) {
              // fallback if timestamp missing but a date string exists
              evDate = Timestamp.fromDate(new Date(event.date.slice(0,4)+'-'+event.date.slice(4,6)+'-'+event.date.slice(6,8))).toDate();
            }
          } catch (e) {
            evDate = null;
          }

          if (evDate) {
            if (evDate.getTime() < startDate.getTime()) {
              // skip events that are older than the requested range
              skippedCount++;
              return;
            }
          } else {
            // If no event timestamp available, conservatively include the document (so it isn't silently dropped),
            // but note this may slightly inflate counts for edge cases where storedAt differs from the event time.
          }
        }

        const earnings = event.cpi * event.eventCount;
        totalEarnings += earnings;
        totalInstalls += event.eventCount;
        processedCount++;
        
        if (processedCount <= 5) {
          console.log('[useInstallEventsAggregation] Event processed:', { 
            docId: doc.id, 
            game: event.game, 
            cpi: event.cpi, 
            eventCount: event.eventCount, 
            earnings: earnings.toFixed(4),
            runningTotal: totalEarnings.toFixed(4)
          });
        }
      });

      console.log('[useInstallEventsAggregation] Final totals:', { 
        userId, 
        filter, 
        gameFilter, 
        totalEarnings: totalEarnings.toFixed(4), 
        totalInstalls, 
        processedCount,
        skippedCount 
      });

      setData({
        totalEarnings,
        totalInstalls,
        loading: false,
      });
    }, (error) => {
      console.error('Error aggregating install events:', error);
      setData({ totalEarnings: 0, totalInstalls: 0, loading: false });
    });

    return () => unsubscribe();
  }, [userId, filter, gameFilter]);

  return data;
}
